import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Award, ArrowRight, Sparkles, Share2, BarChart3, AlertCircle } from "lucide-react";
import { WhatsAppFloating } from "../components/WhatsApp";

const STATUS = {
    pending: { cls: "border-zinc-700 text-zinc-300", l: "Sponsoring envoyé" },
    club_approved: { cls: "border-green-800 text-green-400", l: "Actif" },
    club_refused: { cls: "border-red-900 text-red-400", l: "Refusé" },
    in_production: { cls: "border-orange-500/40 text-orange-500", l: "En production" },
    delivered: { cls: "border-green-800 text-green-400", l: "Livré" },
};

const ICONS_COLOR = {
    silver: "#a1a1aa",
    gold: "#eab308",
    diamond: "#06b6d4",
    premium: "#a855f7",
};

export default function DashboardSponsor() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loyalty, setLoyalty] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/sponsorships/mine").then((r) => setItems(r.data.items || [])).catch(() => {}),
            api.get("/sponsorships/loyalty").then((r) => setLoyalty(r.data)).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, []);

    const lvl = loyalty?.current_level;
    const lvlColor = lvl ? ICONS_COLOR[lvl.key] : "#a1a1aa";
    const isPending = user && user.status !== "approved";
    const isDiamondPlus = lvl && (lvl.key === "diamond" || lvl.key === "premium");

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-8">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                        Espace sponsor
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
                        Mes sponsorings
                    </h1>
                </div>
                {loyalty && (
                    <Link
                        to="/dashboard/sponsor/loyalty"
                        data-testid="loyalty-link"
                        className="card-tu flex items-center gap-4 hover:border-orange-500/50 transition-colors min-w-[260px]"
                        style={{ borderColor: `${lvlColor}55` }}
                    >
                        <div
                            className="w-12 h-12 flex items-center justify-center border-2"
                            style={{ borderColor: lvlColor, color: lvlColor }}
                        >
                            <Award size={20} />
                        </div>
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                                Niveau {lvl?.label}
                            </div>
                            <div className="font-display text-2xl" style={{ color: lvlColor }}>
                                {loyalty.points} pts
                            </div>
                        </div>
                        <ArrowRight size={16} className="text-orange-500" />
                    </Link>
                )}
            </header>

            {/* Pending validation banner */}
            {isPending && (
                <div data-testid="sponsor-pending-banner" className="card-tu border-l-4 border-l-orange-500 flex items-start gap-3">
                    <AlertCircle className="text-orange-500 shrink-0" />
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                            Compte en attente de validation
                        </div>
                        <p className="text-zinc-300 text-sm">
                            Votre SIREN est en cours de vérification par l'administration. Vous pourrez consulter
                            les tarifs et lancer vos sponsorings une fois votre compte approuvé.
                        </p>
                    </div>
                </div>
            )}

            {/* Quick links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Link
                    to="/dashboard/sponsor/referral"
                    data-testid="referral-link"
                    className="card-tu hover:border-orange-500/50 flex items-center gap-3 transition-colors"
                >
                    <Share2 className="text-orange-500" size={20} />
                    <div className="flex-1">
                        <div className="font-display text-xl uppercase tracking-tight">Parrainage</div>
                        <div className="text-xs text-zinc-500">+100 pts par filleul</div>
                    </div>
                    <ArrowRight size={16} className="text-orange-500" />
                </Link>
                <Link
                    to="/dashboard/sponsor/analytics"
                    data-testid="analytics-link"
                    className={[
                        "card-tu flex items-center gap-3 transition-colors",
                        isDiamondPlus
                            ? "hover:border-orange-500/50"
                            : "opacity-60 hover:border-zinc-700",
                    ].join(" ")}
                >
                    <BarChart3 className="text-orange-500" size={20} />
                    <div className="flex-1">
                        <div className="font-display text-xl uppercase tracking-tight">Analytics</div>
                        <div className="text-xs text-zinc-500">
                            {isDiamondPlus ? "Visibilité, ROI, audience" : "Réservé Diamond+"}
                        </div>
                    </div>
                    <ArrowRight size={16} className="text-orange-500" />
                </Link>
                <div className="card-tu border-l-4 border-l-orange-500 flex items-start gap-3">
                    <Sparkles size={18} className="text-orange-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-zinc-300">
                        <span className="font-bold text-orange-500">Astuce :</span> remise auto de
                        30% au-delà de 100 maillots du même club.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="text-zinc-500">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="card-tu text-center py-16">
                    <p className="text-zinc-400 mb-6">Vous n'avez pas encore sponsorisé de club.</p>
                    <Link to="/clubs" data-testid="explore-clubs" className="btn-primary">
                        Trouver un club
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((s) => {
                        const st = STATUS[s.status] || { cls: "border-zinc-700 text-zinc-300", l: s.status };
                        return (
                            <div key={s.id} className="card-tu" data-testid={`sponsorship-${s.id}`}>
                                <div className="flex items-center gap-4">
                                    <div className="bg-zinc-950 border border-zinc-800 w-20 h-20 flex items-center justify-center shrink-0">
                                        <img src={fileUrl(s.logo_path)} alt="logo" className="max-w-full max-h-full object-contain" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs uppercase tracking-widest text-zinc-500">
                                            {s.club_name}
                                        </div>
                                        <div className="font-display text-2xl">{s.sponsor_company}</div>
                                        <div className="text-xs text-zinc-400 mt-1">
                                            {s.age_category} · {s.zones?.map((z) => z.name).join(", ")}
                                        </div>
                                        <div className="text-[11px] text-zinc-500 mt-0.5">
                                            {s.num_players} maillots
                                            {s.discount_pct > 0 && (
                                                <span className="text-orange-500 font-bold ml-2">
                                                    −{s.discount_pct}% remise
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display text-3xl text-orange-500">{s.total_amount}€</div>
                                        <span className={`chip mt-1 ${st.cls}`}>{st.l}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <WhatsAppFloating testid="sponsor-whatsapp-help" />
        </div>
    );
}
